import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import BaseDashboard from '../components/BaseDashboard';
import { createApiUrl } from '../utils/apiConfig';
import { assetFileDisplayName } from '../utils/assetDisplayName';
import RenewalModal from '../components/RenewalModal';
import useImpersonation from '../hooks/useImpersonation';
import StandardPagination from '../components/StandardPagination';
import ImpersonationBanner from '../components/ImpersonationBanner';
import './OrgAdminDashboard.css';

const OrgAdminDashboard = () => {
  const navigate = useNavigate();
  const { isImpersonating, stopImpersonation: _stopImpersonation } = useImpersonation();
  // Impersonation tracking disabled
  const trackButtonClick = () => {};
  const trackDataView = () => {};
  const [user, setUser] = useState(null);

  const [users, setUsers] = useState([]);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [defaultTab, setDefaultTab] = useState('plans');
  
  // Pagination state for users
  const [_currentPage, setCurrentPage] = useState(1);
  const [_totalPages, setTotalPages] = useState(1);
  const usersPerPage = 12; // 4 columns × 3 rows = 12 users per page
  
  // Search and pagination state for users
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userCurrentPage, setUserCurrentPage] = useState(1);
  const [userItemsPerPage, setUserItemsPerPage] = useState(12);
  
  // Pagination state for subscriptions
  const [currentSubPage, setCurrentSubPage] = useState(1);
  const [totalSubPages, setTotalSubPages] = useState(1);
  const subscriptionsPerPage = 6; // 2 columns × 3 rows = 6 subscriptions per page

  const tabs = [
    { id: 'users', label: 'Users' },
    { id: 'subscriptions', label: 'Subscriptions' },
    { id: 'plans', label: 'Plans' },
    { id: 'settings', label: 'Settings' }
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch organization data (user is the organization)
      const orgResponse = await axios.get(createApiUrl('/api/organization'), {
        headers: {
          'Accept': 'application/json'
        },
        withCredentials: true
      });

      // The organization data IS the user data for org admins
      setUser(orgResponse.data);
      
      // Track data view if impersonating
      if (isImpersonating) {
        trackDataView('organization_data', 'organization-dashboard', 'Fetched organization data');
      }
      
      // Fetch users for this organization - request all users (no backend pagination)
      const usersResponse = await axios.get(createApiUrl('/api/users'), {
        headers: {
          'Accept': 'application/json'
        },
        withCredentials: true,
        params: {
          page: 1,
          limit: 1000 // Request a large limit to get all users, frontend will handle pagination
        }
      });

      const allUsers = usersResponse.data.users || [];
      
      const _angelaHall = allUsers.find(u => 
        u.email === 'timberscombe@moorlandfed.co.uk' || 
        u.email?.toLowerCase().includes('timberscombe') ||
        u.contactName?.toLowerCase().includes('angela')
      );
      
      setUsers(allUsers);

      // Track users data view if impersonating
      if (isImpersonating) {
        trackDataView('users_data', 'organization-dashboard', 'Fetched users data');
      }
      
      // Calculate total pages for pagination
      const totalPagesCount = Math.ceil(allUsers.length / usersPerPage);
      setTotalPages(totalPagesCount);
      
      // Fetch subscriptions for this organization
      await fetchSubscriptions(currentSubPage, true); // true = set default tab
      
      // Fetch plans
      const plansResponse = await axios.get(createApiUrl('/api/plans'), {
        headers: {
          'Accept': 'application/json'
        },
        withCredentials: true
      });

      setPlans(plansResponse.data || []);
      
      setLoading(false);
    } catch (error) {
                  setError(error.response?.data?.message || 'Failed to load dashboard data');
      setLoading(false);
      
      // Set fallback data to prevent complete failure
      setUsers([]);
      setSubscriptions([]);
      setPlans([]);
    }
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const handleAddToBasket = async (planId, workshopDate = null) => {
    try {
      // Track action if impersonating
      if (isImpersonating) {
        trackButtonClick('add_to_basket', 'organization-dashboard', `Added plan ${planId} to basket`);
      }

      // For org admins, use the org-add-to-basket endpoint
      const response = await axios.post(createApiUrl(`/api/org-add-to-basket/${user._id}`), {
        planIds: [planId],
        quantity: 1,
        workshopDate: workshopDate
      }, {
        withCredentials: true
      });
      
      if (response.data.success) {
        // Navigate to basket page
        window.location.href = '/basket';
      } else {
        // Error handling removed for production
      }
    } catch (error) {
            // Error handling removed for production
    }
  };

  const handlePurchaseSubscription = () => {
    // Navigate to Plans tab
    setDefaultTab('plans');
  };

  const handleTabChange = (tabId) => {
    // Track tab change if impersonating
    if (isImpersonating) {
      trackButtonClick('tab_change', 'organization-dashboard', `Switched to ${tabId} tab`);
    }
    
    // If user tries to access Subscriptions tab but has no subscriptions, redirect to Plans
    if (tabId === 'subscriptions' && subscriptions.length === 0) {
      setDefaultTab('plans');
      return;
    }
    // Otherwise, allow normal tab change
    setDefaultTab(tabId);
  };

  const _handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Search and pagination functions for users
  const getFilteredUsers = () => {
    if (!userSearchQuery) {
      return users;
    }
    const filtered = users.filter(user => 
      user.contactName?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
      user.schoolName?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
      user.postZipCode?.toLowerCase().includes(userSearchQuery.toLowerCase())
    );
    
    return filtered;
  };

  const getPaginatedUsers = () => {
    const filtered = getFilteredUsers();
    const startIndex = (userCurrentPage - 1) * userItemsPerPage;
    const endIndex = startIndex + userItemsPerPage;
    const paginated = filtered.slice(startIndex, endIndex);
    
    return paginated;
  };

  const handleUserSearch = (e) => {
    e.preventDefault();
    setUserCurrentPage(1);
  };

  const handleUserPageChange = (page) => {
    setUserCurrentPage(page);
  };

  const fetchSubscriptions = async (page = currentSubPage, setDefaultTabFlag = false) => {
    try {

      const subscriptionsResponse = await axios.get(createApiUrl('/api/org-subscriptions'), {
        headers: {
          'Accept': 'application/json'
        },
        withCredentials: true,
        params: {
          page: page,
          limit: subscriptionsPerPage
        }
      });
      
      const subscriptions = subscriptionsResponse.data.subscriptions || [];
      setSubscriptions(subscriptions);
      
      // Set pagination info for subscriptions
      setTotalSubPages(subscriptionsResponse.data.totalPages || 1);
      
      // Set default tab based on whether organization has subscriptions (only on initial load)
      if (setDefaultTabFlag) {
        if (subscriptions.length > 0) {
          setDefaultTab('users');
        } else {
          setDefaultTab('plans');
        }
      }
      
      return subscriptions;
    } catch (error) {
            setSubscriptions([]);
      return [];
    }
  };

  const handleSubPageChange = (page) => {
    setCurrentSubPage(page);
    // Only refetch subscriptions for the new page
    fetchSubscriptions(page);
  };

  const handleAutoRenewChange = async (subscriptionId, isChecked) => {
    try {
      
      const response = await axios.patch(`/api/subscriptions/${subscriptionId}`, {
        autoRenew: isChecked
      }, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });

      if (response.status === 200) {
        // Update the local state to reflect the change
        setSubscriptions(prevSubscriptions => 
          prevSubscriptions.map(sub => 
            sub._id === subscriptionId 
              ? { ...sub, autoRenew: isChecked }
              : sub
          )
        );
      }
    } catch (error) {
            // You might want to show an error message to the user here
    }
  };

  const handleRenewSubscription = (subscription) => {
    setSelectedSubscription(subscription);
    setShowRenewalModal(true);
  };

  const handleRenewalSuccess = () => {
    fetchSubscriptions(currentSubPage);
  };

  const handleCloseRenewalModal = () => {
    setShowRenewalModal(false);
    setSelectedSubscription(null);
  };


  if (error) {
    return (
      <BaseDashboard
        title="Organization Admin Dashboard"
        tabs={tabs}
        user={user}
        loading={false}
      >
        <div className="error-message">
          <div className="alert alert-danger">
            <h4>Error Loading Dashboard</h4>
            <p>{error}</p>
            <button className="btn btn-primary" onClick={fetchData}>
              Retry
            </button>
          </div>
        </div>
      </BaseDashboard>
    );
  }

  return (
    <>
    <BaseDashboard
      title="Organization Admin Dashboard"
      tabs={tabs}
      user={user}
      loading={loading}
      defaultActiveTab={defaultTab}
      onTabChange={handleTabChange}
      banner={<ImpersonationBanner user={user} />}
    >
      {/* Users Tab */}
      <div tabId="users" className="users-tab-content">
        {users.length === 0 ? (
          <div className="text-center">
            <p>No users found. Add users to your organization to get started!</p>
            <button className="btn btn-primary">Add User</button>
          </div>
        ) : (
          <>
            {/* Search Bar - At the Top */}
            <div className="d-flex justify-content-end mb-3">
              <form onSubmit={handleUserSearch} className="form-inline">
                <input
                  className="form-control mr-sm-2"
                  type="search"
                  name="search"
                  placeholder="User Name, Email, School, or Postcode"
                  aria-label="Search"
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  autoComplete="off"
                />
                <button className="btn btn-outline-success my-2 my-sm-0" type="submit">
                  Search
                </button>
              </form>
            </div>
            
            {/* Items Per Page Controls - Below Search */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="d-flex align-items-center">
                  <label htmlFor="usersPerPage" className="form-label me-2 mb-0">Users per page:</label>
                  <select 
                    id="usersPerPage"
                    className="form-select"
                    value={userItemsPerPage}
                    onChange={(e) => setUserItemsPerPage(parseInt(e.target.value))}
                    style={{ 
                      maxWidth: '80px', 
                      height: '32px',
                      fontSize: '14px',
                      padding: '4px 8px'
                    }}
                  >
                    {[10, 20, 50, 100].map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-6 text-end">
                <small style={{ color: 'var(--darkOrange)' }}>
                  Showing {((userCurrentPage - 1) * userItemsPerPage) + 1} to {Math.min(userCurrentPage * userItemsPerPage, getFilteredUsers().length)} of {getFilteredUsers().length} users
                </small>
              </div>
            </div>

            <div className="row" key={`users-${Date.now()}`}>
              {getPaginatedUsers().map((user) => (
                <div key={user._id} className="col-md-3" style={{
                  flex: '0 0 25%',
                  maxWidth: '25%',
                  width: '25%',
                  padding: '0 8px',
                  marginBottom: '20px',
                  boxSizing: 'border-box'
                }}>
                  <div className="card" style={{
                    backgroundColor: 'var(--lightPurple)',
                    color: 'var(--lightOrange)',
                    width: '100%',
                    height: '200px',
                    margin: '0',
                    padding: '0',
                    border: 'none',
                    borderRadius: '5px',
                    boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2)',
                    transition: '0.3s',
                    minWidth: '100%',
                    maxWidth: '100%',
                    overflow: 'hidden'
                  }}>
                    <div className="card-body" style={{ 
                      padding: '10px',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <div className="card-content">
                        <div className="card-info">
                          <h5 className="card-title">{user.contactName}</h5>
                          <p className="card-subtitle">School: {user.schoolName}</p>
                          <p className="card-subtitle" title={user.email}>Email: {user.email}</p>
                        </div>
                      </div>
                      <div className="d-flex justify-content-center" style={{ marginTop: 'auto', paddingTop: '0.5rem' }}>
                        <button 
                          className="btn btn-primary" 
                          style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                          onClick={() => navigate(`/user/${user._id}?from=org-admin`)}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Pagination Controls */}
            {getFilteredUsers().length > 0 && (
              <StandardPagination
                currentPage={userCurrentPage}
                totalPages={Math.ceil(getFilteredUsers().length / userItemsPerPage)}
                onPageChange={handleUserPageChange}
                itemsPerPage={userItemsPerPage}
                onItemsPerPageChange={setUserItemsPerPage}
                totalItems={getFilteredUsers().length}
                itemsPerPageLabel="Users per page:"
                pageInfoLabel="Showing"
                showItemsPerPage={false}
              />
            )}
          </>
        )}
      </div>

      {/* Subscriptions Tab */}
      <div tabId="subscriptions" className="subscriptions-tab-content">
        {subscriptions.length === 0 ? (
          <div className="text-center">
            <p>No subscriptions found. Purchase subscriptions to get started!</p>
            <button className="btn btn-primary" onClick={handlePurchaseSubscription}>Purchase Subscription</button>
          </div>
        ) : (
          <>
            <div className="row">
              {subscriptions.map((subscription) => (
                <div key={subscription._id} className="col-md-4">
                  <div className="card">
                    <div className="card-body">
                      <h5 className="card-title">{subscription.planId?.name || 'Unknown Plan'}</h5>
                      <h6 className="card-subtitle mb-2 text-muted">Status: {subscription.status}</h6>
                      <p className="card-content">
                        <strong>Start Date:</strong> {new Date(subscription.startDate).toLocaleDateString()}<br/>
                        <strong>End Date:</strong> {new Date(subscription.endDate).toLocaleDateString()}<br/>
                        <strong>Quantity:</strong> {subscription.quantity}<br/>
                        <strong>Remaining:</strong> {subscription.remainingQuantity || 0}
                      </p>
                      
                      {subscription.documentUrl && (
                        <div className="mb-2">
                          <a 
                            href={subscription.documentUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="btn btn-sm"
                            style={{
                              backgroundColor: 'var(--darkPurple)',
                              borderColor: 'var(--darkOrange)',
                              color: 'var(--darkOrange)',
                              fontWeight: 'bold'
                            }}
                          >
                            📄 View Invoice/Receipt
                          </a>
                        </div>
                      )}
                      
                      <div className="d-flex justify-content-between align-items-center">
                        {subscription.planId?.type !== 'workshop' && (
                          <div className="form-check">
                            <input 
                              className="form-check-input" 
                              type="checkbox" 
                              id={`autoRenew-${subscription._id}`}
                              checked={subscription.autoRenew !== false}
                              onChange={(e) => handleAutoRenewChange(subscription._id, e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor={`autoRenew-${subscription._id}`}>
                              Auto Renew
                            </label>
                          </div>
                        )}
                        {subscription.planId?.type !== 'workshop' && !subscription.autoRenew && (() => {
                          const endDate = new Date(subscription.endDate);
                          const currentDate = new Date();
                          const thirtyDaysFromNow = new Date(currentDate.getTime() + (30 * 24 * 60 * 60 * 1000));
                          const shouldShowRenew = endDate <= thirtyDaysFromNow;
                          return shouldShowRenew;
                        })() && (
                          <button 
                            className="btn btn-outline-success"
                            onClick={() => handleRenewSubscription(subscription)}
                          >
                            Renew
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {totalSubPages > 1 && (
              <nav aria-label="Subscription page navigation" style={{ marginTop: '20px' }}>
                <ul className="pagination justify-content-center">
                  {Array.from({ length: totalSubPages }, (_, i) => i + 1).map(page => (
                    <li key={page} className={`page-item ${currentSubPage === page ? 'active' : ''}`}>
                      <button className="btn btn-primary page-link" onClick={() => handleSubPageChange(page)}>
                        {page}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </>
        )}
      </div>

      {/* Plans Tab */}
      <div tabId="plans" className="plans-tab-content">
        <h1 className="centered-header">Available Plans</h1>
        <div className="container">
          <div className="subscriptions-row">
            {plans
              .filter((plan) => plan.type !== 'workshop')
              .map((plan) => (
                <div key={plan._id} className="subscriptions-col-md-4">
                  <div className="subscriptions-card overflow-auto">
                    <div className="subscriptions-card-body">
                      <div
                        className={`d-flex ${
                          plan.name === '30-day FREE trial'
                            ? 'justify-content-center'
                            : 'justify-content-between'
                        } mb-3`}
                      >
                        <button
                          className="subscriptions-btn btn btn-primary"
                          onClick={() => handleAddToBasket(plan._id)}
                          disabled={plan.assets.length === 0}
                        >
                          {plan.name === '30-day FREE trial'
                            ? 'Start your Free Trial Now'
                            : 'Buy Subscription'}
                        </button>
                      </div>

                      <h4 className="subscriptions-card-title">{plan.name}</h4>
                      {plan.name !== '30-day FREE trial' && (
                        <>
                          <p>
                            Duration:{' '}
                            {plan.duration < 30
                              ? `${plan.duration} days`
                              : plan.duration % 365 === 0
                              ? `${plan.duration / 365} year${
                                  plan.duration / 365 > 1 ? 's' : ''
                                }`
                              : `${Math.ceil(plan.duration / 30)} months`}
                          </p>
                          <p>Price: {plan.price}</p>
                          {plan.description && <p>Description: {plan.description}</p>}
                        </>
                      )}
                      <br />
                      {plan.type !== 'workshop' && <h5>Lessons:</h5>}
                      <br />
                      <ul className="subscribed-asset">
                        {plan.assets?.map((asset) => (
                          <li key={asset._id}>
                            <h5>{asset.name}</h5>
                            <p>{asset.description}</p>
                            {asset.video?.thumbnail && (
                              <img
                                src={asset.video.thumbnail}
                                alt={`Thumbnail for ${asset.name}`}
                                className="asset-thumbnail thumbnail"
                                data-asset-id={asset._id}
                                style={{ cursor: 'default' }}
                              />
                            )}
                            {asset.file?.url && !asset.video?.thumbnail && (
                              <video
                                height="200"
                                width="280"
                                className="asset-video"
                                id={`video-${asset._id}`}
                                muted
                                loop
                                style={{ pointerEvents: 'none' }}
                              >
                                <source src={asset.file.url} type="video/mp4" />
                                Your browser does not support the video tag.
                              </video>
                            )}
                            {asset.documents?.map((doc, index) => (
                              doc.url && (
                                <div key={index}>
                                  <span
                                    style={{ 
                                      color: '#FFB84D', 
                                      textDecoration: 'none',
                                      cursor: 'default'
                                    }}
                                  >
                                    {assetFileDisplayName(doc)} (Preview only)
                                  </span>
                                  <br />
                                </div>
                              )
                            ))}
                            <hr />
                          </li>
                        ))}
                      </ul>
                      <div className="buy-btns">
                        <div className="button-container">
                          <button
                            className="subscriptions-btn btn btn-primary"
                            onClick={() => handleAddToBasket(plan._id)}
                            disabled={plan.assets.length === 0}
                          >
                            {plan.name === '30-day FREE trial'
                              ? 'Start your Free Trial Now'
                              : 'Buy Subscription'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <br />
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Settings Tab */}
      <div tabId="settings" className="settings-tab-content">
        <div className="row">
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Organization Settings</h5>
                <p className="card-content">
                  <strong>Name:</strong> {user?.name}<br/>
                  <strong>Contact:</strong> {user?.contactName}<br/>
                  <strong>Email:</strong> {user?.email}<br/>
                  <strong>Phone:</strong> {user?.contactPhone || user?.phone}<br/>
                  <strong>Address:</strong> {user?.street && user?.city ? `${user.street}, ${user.city}` : user?.address}<br/>
                  <strong>Post Code:</strong> {user?.postcode || user?.postZipCode}
                </p>
                <button className="btn btn-primary">Edit Settings</button>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Billing Information</h5>
                <p className="card-content">
                  <strong>Stripe Customer ID:</strong> {user?.stripeCustomerId}<br/>
                  <strong>Organization Code:</strong> {user?.code}<br/>
                  <strong>Created:</strong> {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                </p>
                <button className="btn btn-primary">Update Billing</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BaseDashboard>
    
    <RenewalModal
      isOpen={showRenewalModal}
      onClose={handleCloseRenewalModal}
      currentSubscription={selectedSubscription}
      onRenewalSuccess={handleRenewalSuccess}
    />
    </>
  );
};

export default OrgAdminDashboard; 